// Hero.jsx (React 17 compatible) — Contact popup + fun scroll-down + CSS/JS animations only (no framer-motion)
import { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";
import DisplayLottie from "./DisplayLottie";
import Shapes from "./Shapes"; // keep your existing decorative component
import Banking from "../lottie/coding.json";
import SocialMedia from "./SocialMedia";
import ChevronDown from "../icons/chevron-down.svg";

// === Contact Info ===
const CONTACT = {
  phone: "+20 1033998288",
  phoneE164: "201033998288",
  email: "sameermofilfil@gmail.com",
};

const Hero = () => {
  const words = useMemo(
    () => [
      "Consulting",
      "Integration",
      "Database",
      "Automation",
      "Banking",
      "iMAL",
      "PL/SQL",
      "Implementation",
      "Solution",
    ],
    []
  );

  // Typewriter config
  const TYPING_MS = 70;
  const DELETING_MS = 45;
  const HOLD_MS = 1000;

  const [loopIndex, setLoopIndex] = useState(0);
  const [displayWord, setDisplayWord] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Contact modal state
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Mouse parallax state
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const heroRef = useRef(null);

  // (Removed unused scroll progress state and effect)

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setIsContactOpen(false);
    if (isContactOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isContactOpen]);

  // Lock background scroll while modal open
  useEffect(() => {
    if (!isContactOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [isContactOpen]);

  // Typewriter loop
  useEffect(() => {
    const fullWord = words[loopIndex % words.length];
    const target = isDeleting
      ? fullWord.substring(0, displayWord.length - 1)
      : fullWord.substring(0, displayWord.length + 1);

    const atWordDone = !isDeleting && target === fullWord;
    const atWordCleared = isDeleting && target === "";

    const timeout = isDeleting ? DELETING_MS : TYPING_MS;

    const tick = setTimeout(() => {
      setDisplayWord(target);
      if (atWordDone) {
        setTimeout(() => setIsDeleting(true), HOLD_MS);
      } else if (atWordCleared) {
        setIsDeleting(false);
        setLoopIndex((i) => (i + 1) % words.length);
      }
    }, timeout);

    return () => clearTimeout(tick);
  }, [displayWord, isDeleting, loopIndex, words]);

  // Mouse move parallax on the hero area
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setMouse({ x, y });
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  // Copy helpers
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        alert("Copied to clipboard ✔");
      } else {
        // Fallback
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        alert("Copied to clipboard ✔");
      }
    } catch (e) {
      alert("Could not copy, please copy manually.");
    }
  };

  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) setIsContactOpen(false);
  };

  const scrollToNext = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };


  return (
    <HeroStyled
      ref={heroRef}
      className="hero-section"
      role="banner"
      aria-label="Intro section"
      style={{
        // gentle parallax on the radial glow
        '--mx': mouse.x,
        '--my': mouse.y,
      }}
    >
      <Shapes />
      <div className="bg-overlay" aria-hidden="true" />

      <div className="container">
        <div className="hero-grid">
          {/* Left column with CSS entrance animation */}
          <div className="left fade-in-up">
            <span className="badge">Currently</span>
            <h1 className="title" aria-label="Samir Felfel, Technical Consulting Engineer">
            </h1>

            <h2 className="subtitle typewriter" aria-live="polite">
              Technical <span className="highlight">{displayWord}</span>
              <span className="cursor" aria-hidden="true">|</span>
              <span>Engineer</span>
            </h2>

            <p className="">
              I specialize in <strong>core banking implementation</strong>, <strong>integration engineering</strong>, and <strong>PL/SQL automation</strong> — empowering banks to streamline operations with smart, secure, and scalable solutions.
              With <strong>over the years of hands-on experience</strong> in <strong>iMAL Core Banking</strong>, <strong>file-based automation</strong>, and <strong>digital payment integrations</strong>, I bridge the gap between business needs and technical execution.
              Passionate about delivering real results — <strong>faster processing</strong>, <strong>cleaner data</strong>, and <strong>fully automated banking workflows</strong> across leading financial institutions in Africa and the Gulf.
            </p>

            <ul className="value-points" aria-label="Key expertise and achievements">
              <li>Core Banking Integrations: Webservices, File based </li>
              <li>Oracle PL/SQL Procedures & API Automation for iMAL</li>
            </ul>

            <div className="cta-row">
              <a
                className="btn primary"
                href="/assets/SamirCVM2026TC.pdf"
                target="_blank"
                rel="noreferrer"
              >
                Download CV
              </a>
              <button
                className="btn ghost"
                type="button"
                onClick={() => setIsContactOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={isContactOpen}
                aria-controls="contact-modal"
              >
                Contact Me
              </button>
            </div>

            <div className="social-wrap" aria-label="Social media links">
              <SocialMedia />
            </div>
          </div>

          {/* Right column with CSS entrance animation */}
          <div className="right fade-in-up delay-1">
            <div className="lottie-card pop-in" aria-label="Banking animation">
              <DisplayLottie animationData={Banking} />
            </div>

            <div className="quick-stats " aria-label="Quick statistics">
              {[
                { num: "3+", label: "Years in Banking Tech" },
                { num: "2+", label: "Years in Retail POS Tech" },
                { num: "1+", label: "Year in Manufacturer" },
                { num: "12", label: "Core Banking Projects" },
                { num: "4+", label: "Successful Go-Lives" },
              ].map((s, i) => (
                <div key={i} className={`stat reveal-up`} style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="num">{s.num}</div>
                  <div className="label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll down: circular progress + chevron trail */}
      <button
        type="button"
        className="scroll-down"
        onClick={scrollToNext}
        aria-label="Scroll down to next section"
      >
        <div className=" hint chevrons" aria-hidden="true">
          FOR MORE FUN
        </div>

        <div className="chevrons" aria-hidden="true">

          <img src={ChevronDown} alt="Scroll" />
          <img src={ChevronDown} alt="Scroll" />
          <img src={ChevronDown} alt="Scroll" />
        </div>
        <span className="hint">Scroll  down</span>
      </button>

      {/* Portal-mounted modal */}
      {isContactOpen &&
        ReactDOM.createPortal(
          <ModalOverlay onMouseDown={onOverlayClick}>
            <ModalCard
              role="dialog"
              aria-modal="true"
              aria-labelledby="contact-title"
            >
              <button
                className="close"
                type="button"
                aria-label="Close contact panel"
                onClick={() => setIsContactOpen(false)}
              >
                ×
              </button>

              <h3 id="contact-title">Contact Samir</h3>
              <p className="desc">Reach me directly via phone, WhatsApp, or email.</p>

              <div className="contact-grid">
                {/* Phone */}
                <div className="contact-card">
                  <div className="label">Phone</div>
                  <div className="value">{CONTACT.phone}</div>
                  <div className="actions">
                    <a
                      className="btn ghost"
                      href={"https://wa.me/" + CONTACT.phoneE164}
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp
                    </a>
                    <button className="btn ghost" type="button" onClick={() => copyToClipboard(CONTACT.phone)}>
                      Copy
                    </button>
                  </div>
                </div>

                {/* Email */}
                <div className="contact-card">
                  <div className="label">Email</div>
                  <div className="value">{CONTACT.email}</div>
                  <div className="actions">
                    <button className="btn ghost" type="button" onClick={() => copyToClipboard(CONTACT.email)}>
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <p className="hint">Note: Happy to work with you.</p>
            </ModalCard>
          </ModalOverlay>,
          document.body
        )}
    </HeroStyled>
  );
};

// ====== Styled Components & keyframes ======
const floaty = keyframes`
  0% { transform: translate3d(0,0,0); }
  50% { transform: translate3d(0,-6px,0); }
  100% { transform: translate3d(0,0,0); }
`;

const glowPulse = keyframes`
  0%,100% { opacity: .5; }
  50% { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 22px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(.96); }
  to { opacity: 1; transform: scale(1); }
`;

const revealUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeroStyled = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  isolation: isolate;
  background: radial-gradient(1200px circle at calc(var(--mx, .5) * 100%) calc(var(--my, .5) * 100%), rgba(255,255,255,.14), transparent 40%), linear-gradient(45deg, #0a74da, #11cdef);
  color: #fff;
  overflow: hidden;

  .bg-overlay {
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.05),
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px,
      transparent 10px
    );
    pointer-events: none;
    z-index: 0;
    mix-blend-mode: overlay;
    animation: ${glowPulse} 5s ease-in-out infinite;
  }

  .container { width: min(1200px, 92%); margin: 0 auto; z-index: 1; }

  .hero-grid { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: clamp(24px, 6vw, 48px); align-items: center; }

  .left { display: flex; flex-direction: column; gap: 13px; }

  .badge {
    display: inline-block; width: fit-content; background: rgba(255, 255, 255, 0.14);
    padding: 6px 12px; border-radius: 999px; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase;
    backdrop-filter: blur(6px); animation: ${floaty} 4s ease-in-out infinite;
  }

  .title { font-size: clamp(36px, 5vw, 56px); line-height: 1.1; margin: 0; font-weight: 800; }
  .subtitle { font-size: clamp(16px, 2.2vw, 22px); font-weight: 600; opacity: .98; margin: 0 0 6px 0; min-height: 1.6em; }
  .highlight { color: #ffe44d; text-shadow: 0 4px 30px rgba(255,228,77,.35); }
  .typewriter .cursor { display: inline-block; margin-left: 2px; animation: blink 1s steps(2, start) infinite; opacity: .9; }
  @keyframes blink { 0%, 49% { opacity: 0; } 50%, 100% { opacity: 1; } }

  .lede { font-size: clamp(16px, 2vw, 20px); line-height: 1.6; opacity: .95; margin: 0; }
  .value-points { margin: 6px 0 8px 0; padding-left: 18px; display: grid; gap: 6px; }

  .cta-row { display: flex; gap: 12px; margin-top: 8px; }

  .btn { --ring: rgba(255, 255, 255, 0.45); display: inline-flex; align-items: center; justify-content: center; height: 44px; padding: 0 16px; border-radius: 12px; font-weight: 700; text-decoration: none; transition: transform .15s ease, box-shadow .15s ease, background .2s ease; will-change: transform; cursor: pointer; border: none; }
  .btn:focus-visible { outline: 3px solid var(--ring); outline-offset: 2px; }
  .btn.primary { background: #fff; color: #0a5fd8; box-shadow: 0 10px 25px rgba(0,0,0,.18); }
  .btn.primary:hover { transform: translateY(-1px); }
  .btn.ghost { border: 2px solid rgba(255,255,255,.55); background: transparent; color: #fff; }
  .btn.ghost:hover { background: rgba(255,255,255,.08); transform: translateY(-1px); }

  .social-wrap { margin-top: 8px; }

  .right { display: grid; gap: 16px; }

  .lottie-card { width: min(520px, 100%); padding: 10px; background: rgba(255,255,255,.1); border-radius: 20px; box-shadow: 0 20px 60px rgba(3,20,44,.3); backdrop-filter: blur(8px); }

  .quick-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
  .stat { background: rgba(255,255,255,.08); padding: 14px 16px; border-radius: 16px; text-align: center; backdrop-filter: blur(6px); animation: ${revealUp} .55s ease-out both; }
  .num { font-size: clamp(18px, 3.2vw, 28px); font-weight: 800; }
  .label { font-size: 12px; opacity: .9; }

  .fade-in-up { animation: ${fadeInUp} .6s ease-out both; }
  .delay-1 { animation-delay: .08s; }
  .pop-in { animation: ${popIn} .5s ease-out both; }

  /* Fun scroll-down button */
  .scroll-down { position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%); cursor: pointer; color: #fff; opacity: .95; display: inline-flex; align-items: center; justify-content: center; gap: 10px; border: 0; background: transparent; filter: drop-shadow(0 6px 16px rgba(0,0,0,.3)); }
  .scroll-down .hint { font-weight: 700; letter-spacing: .08em; text-transform: uppercase; font-size: 12px; opacity: .9; }
  .scroll-down .progress { width: 44px; height: 44px; transform: rotate(-90deg); }
  .scroll-down .progress .track { fill: none; stroke: rgba(255,255,255,.28); stroke-width: 3; }
  .scroll-down .progress .indicator { fill: none; stroke: #fff; stroke-width: 3; stroke-linecap: round; transition: stroke-dashoffset .2s ease; }

  .chevrons { display: grid; place-items: center; }
  .chevrons img { width: 28px; height: 28px; opacity: .95; animation: chevy 1.6s infinite; }
  .chevrons img:nth-child(2) { animation-delay: .15s; opacity: .8; }
  .chevrons img:nth-child(3) { animation-delay: .3s; opacity: .6; }
  @keyframes chevy { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(6px); } }

  @media (max-width: 968px) {
    .hero-grid { grid-template-columns: 1fr; }
    .right { order: -1; }
    .lottie-card { justify-self: center; }
    .quick-stats { grid-template-columns: repeat(3, minmax(0,1fr)); }
  }

  @media (max-width: 768px) {
    padding-top: 120px; padding-bottom: 60px;
    .quick-stats { grid-template-columns: repeat(2, minmax(0,1fr)); }
  }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(3,20,44,.55);
  -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);
  display: grid; place-items: center; z-index: 2147483647; padding: 16px;
`;

const ModalCard = styled.div`
  position: relative; width: min(560px, 100%);
  background: #0b2b4d; color: #fff; border-radius: 20px;
  box-shadow: 0 30px 80px rgba(0,0,0,.45); padding: 22px 22px 20px 22px;
  border: 1px solid rgba(255,255,255,.12);

  h3 { margin: 0 0 4px 0; font-size: clamp(20px, 2.2vw, 24px); }
  .desc { margin: 0 0 16px 0; opacity: .9; }

  .contact-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
  .contact-card { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.15); border-radius: 14px; padding: 12px; }
  .label { font-size: 12px; letter-spacing: .06em; text-transform: uppercase; opacity: .85; }
  .value { font-size: clamp(16px, 2vw, 18px); font-weight: 700; margin: 4px 0 10px; word-break: break-word; }
  .actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .btn { height: 36px; border-radius: 10px; font-weight: 700; padding: 0 12px; cursor: pointer; border: none; }
  .btn.primary { background: #fff; color: #0a5fd8; }
  .btn.ghost { border: 2px solid rgba(255,255,255,.55); color: #fff; background: transparent; }

  .hint { margin-top: 12px; font-size: 12px; opacity: .8; }

  .close { position: absolute; top: 8px; right: 10px; width: 36px; height: 36px; border-radius: 999px; border: none; background: rgba(255,255,255,.1); color: #fff; font-size: 22px; cursor: pointer; line-height: 1; }
  .close:hover { background: rgba(255,255,255,.18); }
`;

export default Hero;
